import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plane, Building2, Calendar, Users, Search, ArrowRight, 
  Clock, Star, MapPin, Loader2, ChevronRight, Sun, Cloud,
  Thermometer, Camera, Heart, Compass, Globe, Sparkles,
  Mountain, Palmtree, Castle, Tent, Anchor, Umbrella,
  Baby, Waves, Coffee, Wine, Footprints, Map, CloudSun
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { FloatingBackButton } from '../layout/FloatingBackButton';
import WeatherWidget from '../weather/WeatherWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extended destination images for dynamic backgrounds
const DESTINATION_IMAGES = {
  // India
  'DEL': 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1920&q=80',
  'BOM': 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1920&q=80',
  'BLR': 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=1920&q=80',
  'GOI': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920&q=80',
  'JAI': 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=1920&q=80',
  'KEP': 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1920&q=80',
  // International
  'DXB': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80',
  'SIN': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
  'LHR': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80',
  'PAR': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  'CDG': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  'JFK': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=80',
  'NYC': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=80',
  'BKK': 'https://images.unsplash.com/photo-1508009603885-50cf7c8dd0d5?w=1920&q=80',
  'TYO': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80',
  'NRT': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80',
  'SYD': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&q=80',
  'ROM': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=80',
  'FCO': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=80',
  'BCN': 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=1920&q=80',
  'AMS': 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=1920&q=80',
  'IST': 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1920&q=80',
  'MLE': 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1920&q=80',
  'BAL': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1920&q=80',
  'EDI': 'https://images.unsplash.com/photo-1506377585622-bedcbb027afc?w=1920&q=80',
  'BUH': 'https://images.unsplash.com/photo-1584646098378-0874589d76b1?w=1920&q=80',
  'default': 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80'
};

// Extended destination info
const DESTINATION_INFO = {
  'DXB': { name: 'Dubai', country: 'UAE', weather: '35°C Sunny', bestTime: 'Nov - Apr', currency: 'AED', vibe: 'Luxury & Adventure' },
  'SIN': { name: 'Singapore', country: 'Singapore', weather: '31°C Humid', bestTime: 'Feb - Apr', currency: 'SGD', vibe: 'Modern & Cultural' },
  'LHR': { name: 'London', country: 'United Kingdom', weather: '18°C Cloudy', bestTime: 'May - Sep', currency: 'GBP', vibe: 'Historic & Iconic' },
  'PAR': { name: 'Paris', country: 'France', weather: '22°C Pleasant', bestTime: 'Apr - Jun', currency: 'EUR', vibe: 'Romantic & Artistic' },
  'CDG': { name: 'Paris', country: 'France', weather: '22°C Pleasant', bestTime: 'Apr - Jun', currency: 'EUR', vibe: 'Romantic & Artistic' },
  'BKK': { name: 'Bangkok', country: 'Thailand', weather: '33°C Hot', bestTime: 'Nov - Feb', currency: 'THB', vibe: 'Vibrant & Exotic' },
  'TYO': { name: 'Tokyo', country: 'Japan', weather: '25°C Clear', bestTime: 'Mar - May', currency: 'JPY', vibe: 'Futuristic & Traditional' },
  'NRT': { name: 'Tokyo', country: 'Japan', weather: '25°C Clear', bestTime: 'Mar - May', currency: 'JPY', vibe: 'Futuristic & Traditional' },
  'DEL': { name: 'Delhi', country: 'India', weather: '38°C Hot', bestTime: 'Oct - Mar', currency: 'INR', vibe: 'Heritage & Diversity' },
  'BOM': { name: 'Mumbai', country: 'India', weather: '32°C Humid', bestTime: 'Nov - Feb', currency: 'INR', vibe: 'Dynamic & Coastal' },
  'GOI': { name: 'Goa', country: 'India', weather: '30°C Tropical', bestTime: 'Nov - Feb', currency: 'INR', vibe: 'Beach & Nightlife' },
  'MLE': { name: 'Maldives', country: 'Maldives', weather: '29°C Tropical', bestTime: 'Dec - Apr', currency: 'MVR', vibe: 'Paradise & Luxury' },
  'BAL': { name: 'Bali', country: 'Indonesia', weather: '28°C Tropical', bestTime: 'Apr - Oct', currency: 'IDR', vibe: 'Spiritual & Scenic' },
  'EDI': { name: 'Edinburgh', country: 'Scotland', weather: '15°C Cool', bestTime: 'May - Sep', currency: 'GBP', vibe: 'Historic & Mystical' },
  'BUH': { name: 'Bucharest', country: 'Romania', weather: '20°C Pleasant', bestTime: 'May - Sep', currency: 'RON', vibe: 'Cultural & Charming' },
};

// Trending destinations
const TRENDING_DESTINATIONS = [
  { code: 'DXB', city: 'Dubai', country: 'UAE', price: '₹15,999', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80', tag: 'Trending' },
  { code: 'SIN', city: 'Singapore', country: 'Singapore', price: '₹18,499', image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80', tag: 'Popular' },
  { code: 'BKK', city: 'Bangkok', country: 'Thailand', price: '₹12,999', image: 'https://images.unsplash.com/photo-1508009603885-50cf7c8dd0d5?w=800&q=80', tag: 'Budget' },
  { code: 'MLE', city: 'Maldives', country: 'Maldives', price: '₹45,999', image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80', tag: 'Honeymoon' },
  { code: 'PAR', city: 'Paris', country: 'France', price: '₹42,999', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80', tag: 'Romantic' },
  { code: 'TYO', city: 'Tokyo', country: 'Japan', price: '₹38,999', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80', tag: 'Culture' },
];

// Hidden Gems - Lesser known destinations
const HIDDEN_GEMS = [
  { city: 'Hampi', country: 'India', desc: 'Ancient ruins & boulder landscapes', price: '₹8,999', image: 'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?w=800&q=80' },
  { city: 'Ljubljana', country: 'Slovenia', desc: 'Green capital with dragon bridges', price: '₹35,999', image: 'https://images.unsplash.com/photo-1569144157591-c60f3f82f137?w=800&q=80' },
  { city: 'Kotor', country: 'Montenegro', desc: 'Medieval town meets fjords', price: '₹32,999', image: 'https://images.unsplash.com/photo-1555990538-1e7afd4c5a58?w=800&q=80' },
  { city: 'Tbilisi', country: 'Georgia', desc: 'Wine, culture & ancient churches', price: '₹28,999', image: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=800&q=80' },
];

// Luxury Escapes
const LUXURY_ESCAPES = [
  { city: 'Santorini', country: 'Greece', type: 'Private Villa', price: '₹1,25,000', image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80', nights: '5 Nights' },
  { city: 'Maldives', country: 'Maldives', type: 'Overwater Bungalow', price: '₹2,50,000', image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80', nights: '4 Nights' },
  { city: 'Swiss Alps', country: 'Switzerland', type: 'Chalet Suite', price: '₹1,80,000', image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80', nights: '5 Nights' },
];

// Beach Destinations
const BEACH_DESTINATIONS = [
  { city: 'Bali', country: 'Indonesia', beach: 'Nusa Dua', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80', price: '₹32,999' },
  { city: 'Phuket', country: 'Thailand', beach: 'Patong Beach', image: 'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=800&q=80', price: '₹22,999' },
  { city: 'Goa', country: 'India', beach: 'Palolem Beach', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=800&q=80', price: '₹8,999' },
  { city: 'Cancun', country: 'Mexico', beach: 'Playa Delfines', image: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=800&q=80', price: '₹65,999' },
  { city: 'Zanzibar', country: 'Tanzania', beach: 'Nungwi Beach', image: 'https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?w=800&q=80', price: '₹55,999' },
];

// Cultural Journeys
const CULTURAL_JOURNEYS = [
  { city: 'Kyoto', country: 'Japan', highlight: 'Ancient Temples & Geisha District', image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80', duration: '7 Days' },
  { city: 'Varanasi', country: 'India', highlight: 'Spiritual Heart of India', image: 'https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=800&q=80', duration: '4 Days' },
  { city: 'Marrakech', country: 'Morocco', highlight: 'Medinas & Souks', image: 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=80', duration: '5 Days' },
  { city: 'Rome', country: 'Italy', highlight: 'Ancient History & Art', image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80', duration: '6 Days' },
];

// Adventure Experiences
const ADVENTURE_EXPERIENCES = [
  { title: 'Northern Lights Hunt', location: 'Iceland', type: 'Aurora Viewing', image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80', price: '₹95,000' },
  { title: 'Safari Adventure', location: 'Kenya', type: 'Wildlife Safari', image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80', price: '₹1,20,000' },
  { title: 'Everest Base Camp', location: 'Nepal', type: 'Trekking', image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80', price: '₹85,000' },
  { title: 'Scuba Diving', location: 'Great Barrier Reef', type: 'Underwater', image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80', price: '₹75,000' },
];

// Family Travel
const FAMILY_DESTINATIONS = [
  { city: 'Orlando', country: 'USA', attraction: 'Disney World & Universal', image: 'https://images.unsplash.com/photo-1575237180893-cb6fcf3b5f22?w=800&q=80', tag: 'Theme Parks' },
  { city: 'Singapore', country: 'Singapore', attraction: 'Universal & Sentosa', image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80', tag: 'Family Fun' },
  { city: 'Dubai', country: 'UAE', attraction: 'Legoland & Aquaventure', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80', tag: 'Adventure' },
  { city: 'Kerala', country: 'India', attraction: 'Backwaters & Wildlife', image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=800&q=80', tag: 'Nature' },
];

// Weekend Getaways
const WEEKEND_GETAWAYS = [
  { city: 'Goa', price: '₹4,999', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600&q=80', duration: '2N/3D' },
  { city: 'Jaipur', price: '₹3,499', image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&q=80', duration: '2N/3D' },
  { city: 'Udaipur', price: '₹5,999', image: 'https://images.unsplash.com/photo-1568495248636-6432b97bd949?w=600&q=80', duration: '2N/3D' },
  { city: 'Rishikesh', price: '₹2,999', image: 'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?w=600&q=80', duration: '2N/3D' },
];

// Travel Stories / Editorial Content
const TRAVEL_STORIES = [
  {
    title: "48 Hours in Tokyo: A Journey Through Time",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&q=80",
    readTime: "8 min read",
    category: "City Guide"
  },
  {
    title: "The Secret Beaches of Thailand Nobody Talks About",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=1200&q=80",
    readTime: "6 min read",
    category: "Hidden Gems"
  },
  {
    title: "Why Bali Remains the World's Favorite Island",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80",
    readTime: "5 min read",
    category: "Destinations"
  },
];

// Popular airports
const POPULAR_AIRPORTS = [
  { code: 'DEL', city: 'New Delhi', name: 'Indira Gandhi International', country: 'India' },
  { code: 'BOM', city: 'Mumbai', name: 'Chhatrapati Shivaji Maharaj', country: 'India' },
  { code: 'BLR', city: 'Bengaluru', name: 'Kempegowda International', country: 'India' },
  { code: 'DXB', city: 'Dubai', name: 'Dubai International', country: 'UAE' },
  { code: 'SIN', city: 'Singapore', name: 'Changi Airport', country: 'Singapore' },
  { code: 'LHR', city: 'London', name: 'Heathrow', country: 'UK' },
  { code: 'BKK', city: 'Bangkok', name: 'Suvarnabhumi', country: 'Thailand' },
  { code: 'PAR', city: 'Paris', name: 'Charles de Gaulle', country: 'France' },
  { code: 'JFK', city: 'New York', name: 'John F. Kennedy', country: 'USA' },
  { code: 'TYO', city: 'Tokyo', name: 'Narita International', country: 'Japan' },
  { code: 'BAL', city: 'Bali', name: 'Ngurah Rai International', country: 'Indonesia' },
  { code: 'EDI', city: 'Edinburgh', name: 'Edinburgh Airport', country: 'Scotland' },
  { code: 'BUH', city: 'Bucharest', name: 'Henri Coanda', country: 'Romania' },
];

// Section Header Component
const SectionHeader = ({ eyebrow, title, subtitle, accentColor = '#00E5FF', align = 'left' }) => (
  <div className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}>
    <p className="text-sm uppercase tracking-[0.3em] mb-3 font-medium" style={{ color: accentColor }}>
      {eyebrow}
    </p>
    <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white font-bold mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-white/50 max-w-2xl">{subtitle}</p>}
  </div>
);

// Destination Card Component
const DestinationCard = ({ destination, size = 'normal', onClick }) => {
  const sizeClasses = {
    large: 'md:col-span-2 md:row-span-2 min-h-[400px]',
    normal: 'min-h-[250px]',
    wide: 'md:col-span-2 min-h-[200px]'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl cursor-pointer group ${sizeClasses[size]}`}
      data-testid={`destination-${destination.code || destination.city}`}
    >
      <img 
        src={destination.image}
        alt={destination.city}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      
      {destination.tag && (
        <div className="absolute top-4 left-4">
          <span className="bg-[#FF007F] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,0,127,0.5)]">
            {destination.tag}
          </span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className={`font-['Playfair_Display'] text-white font-bold mb-1 ${size === 'large' ? 'text-4xl' : 'text-2xl'}`}>
          {destination.city}
        </h3>
        <p className="text-white/60 mb-3">{destination.country}</p>
        {destination.price && (
          <div className="flex items-center justify-between">
            <p className="text-[#00E5FF] font-semibold">From {destination.price}</p>
            <div className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center group-hover:bg-[#00E5FF] transition-all shadow-[0_0_20px_rgba(0,0,0,0.3)]">
              <ArrowRight className="w-5 h-5 text-white" />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function PremiumTravelSection() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('explore');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [heroImage, setHeroImage] = useState(DESTINATION_IMAGES.default);
  const [destinationInfo, setDestinationInfo] = useState(null);
  const [activeCollection, setActiveCollection] = useState('trending');
  
  // Form states
  const [flightForm, setFlightForm] = useState({
    origin: '',
    destination: '',
    departure_date: '',
    return_date: '',
    adults: 1,
    travel_class: 'ECONOMY',
    trip_type: 'one-way' // 'one-way' or 'round-trip'
  });
  
  // Currency state - detect from user's location
  const [userCurrency, setUserCurrency] = useState('INR');
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  
  // Search animation state
  const [searchingAnimation, setSearchingAnimation] = useState(false);
  
  const [hotelForm, setHotelForm] = useState({
    city: '',
    check_in: '',
    check_out: '',
    guests: 2,
    rooms: 1
  });
  
  // Suggestions
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  // Currency detection based on user location
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Try to detect user's country from timezone or navigator
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = navigator.language || 'en-IN';
        
        const currencyMap = {
          'Asia/Kolkata': { currency: 'INR', symbol: '₹' },
          'Asia/Calcutta': { currency: 'INR', symbol: '₹' },
          'America/New_York': { currency: 'USD', symbol: '$' },
          'America/Los_Angeles': { currency: 'USD', symbol: '$' },
          'America/Chicago': { currency: 'USD', symbol: '$' },
          'Europe/London': { currency: 'GBP', symbol: '£' },
          'Europe/Paris': { currency: 'EUR', symbol: '€' },
          'Europe/Berlin': { currency: 'EUR', symbol: '€' },
          'Asia/Tokyo': { currency: 'JPY', symbol: '¥' },
          'Asia/Singapore': { currency: 'SGD', symbol: 'S$' },
          'Asia/Dubai': { currency: 'AED', symbol: 'د.إ' },
          'Australia/Sydney': { currency: 'AUD', symbol: 'A$' },
          'Asia/Bangkok': { currency: 'THB', symbol: '฿' },
          'Asia/Hong_Kong': { currency: 'HKD', symbol: 'HK$' },
        };
        
        if (currencyMap[timezone]) {
          setUserCurrency(currencyMap[timezone].currency);
          setCurrencySymbol(currencyMap[timezone].symbol);
        } else if (locale.includes('en-US')) {
          setUserCurrency('USD');
          setCurrencySymbol('$');
        } else if (locale.includes('en-GB')) {
          setUserCurrency('GBP');
          setCurrencySymbol('£');
        }
      } catch (e) {
        console.log('Currency detection fallback to INR');
      }
    };
    detectCurrency();
  }, []);

  // Update hero image when destination changes
  useEffect(() => {
    const destCode = activeTab === 'flights' 
      ? flightForm.destination.toUpperCase().substring(0, 3)
      : hotelForm.city.toUpperCase().substring(0, 3);
    
    if (destCode && DESTINATION_IMAGES[destCode]) {
      setHeroImage(DESTINATION_IMAGES[destCode]);
      setDestinationInfo(DESTINATION_INFO[destCode] || null);
    } else if (destCode.length >= 3) {
      const match = POPULAR_AIRPORTS.find(a => 
        a.city.toLowerCase().includes(destCode.toLowerCase()) ||
        a.code.toLowerCase().includes(destCode.toLowerCase())
      );
      if (match && DESTINATION_IMAGES[match.code]) {
        setHeroImage(DESTINATION_IMAGES[match.code]);
        setDestinationInfo(DESTINATION_INFO[match.code] || null);
      }
    }
  }, [flightForm.destination, hotelForm.city, activeTab]);

  const [airportSearchResults, setAirportSearchResults] = useState([]);
  const [airportSearchLoading, setAirportSearchLoading] = useState(false);
  
  // Debounced airport search function
  const searchAirportsAPI = async (query) => {
    if (!query || query.length < 1) {
      setAirportSearchResults([]);
      return;
    }
    setAirportSearchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/tripjack/airports`, {
        params: { query }
      });
      if (response.data.success) {
        setAirportSearchResults(response.data.airports || []);
      } else {
        setAirportSearchResults([]);
      }
    } catch (err) {
      console.error('Airport search error:', err);
      setAirportSearchResults([]);
    } finally {
      setAirportSearchLoading(false);
    }
  };

  // Debounce timer ref
  const searchTimeoutRef = useRef(null);
  
  // Hotel selection loading state
  const [hotelLoading, setHotelLoading] = useState(null); // null or hotel.id being loaded

  // Handle hotel selection - call pricing API and navigate to booking
  const handleSelectHotel = async (hotel) => {
    if (hotelLoading) return; // Prevent double-click
    setHotelLoading(hotel.hotelId || hotel.id);
    
    try {
      toast.info('Getting room details...');
      
      // Call pricing API to get reviewHash
      const pricingResponse = await axios.post(
        `${API_URL}/api/tripjack/hotels/pricing`,
        {
          correlation_id: hotel.correlationId,
          hotel_id: String(hotel.hotelId || hotel.id),
          checkin_date: hotel.searchParams?.checkinDate || hotelForm.check_in,
          checkout_date: hotel.searchParams?.checkoutDate || hotelForm.check_out,
          adults: hotel.searchParams?.adults || hotelForm.guests,
          children: 0,
          rooms: hotel.searchParams?.rooms || hotelForm.rooms || 1
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (!pricingResponse.data.success) {
        toast.error('Failed to get room pricing. Please try again.');
        setHotelLoading(null);
        return;
      }

      const pricingData = pricingResponse.data;
      const reviewHash = pricingData.data?.reviewHash;
      const pricingOptions = pricingData.data?.options || [];
      const firstOption = pricingOptions[0] || {};
      const optionId = firstOption.optionId || hotel.optionId;
      const totalPrice = firstOption.pricing?.totalPrice || hotel.price;

      if (!reviewHash) {
        toast.error('Unable to proceed. Please try again.');
        setHotelLoading(null);
        return;
      }

      // Navigate to complete-booking with correct state structure
      navigate('/complete-booking', {
        state: {
          type: 'hotel',
          selection: {
            correlationId: hotel.correlationId,
            hotelId: String(hotel.hotelId || hotel.id),
            optionId: optionId,
            reviewHash: reviewHash,
            name: hotel.name,
            city: hotel.city || hotelForm.city,
            stars: hotel.stars || hotel.star_rating || 4,
            price: totalPrice,
            currency: hotel.currency || 'INR',
            roomType: hotel.roomType || firstOption.roomInfo?.[0]?.name || 'Standard Room',
            mealBasis: hotel.mealBasis || firstOption.mealBasis || 'Room Only',
            isRefundable: hotel.isRefundable || firstOption.cancellation?.isRefundable || false,
            image: hotel.image,
            checkinDate: hotel.searchParams?.checkinDate || hotelForm.check_in,
            checkoutDate: hotel.searchParams?.checkoutDate || hotelForm.check_out,
            adults: hotel.searchParams?.adults || hotelForm.guests,
            rooms: hotel.searchParams?.rooms || hotelForm.rooms || 1
          },
          pricingData: pricingData.data
        }
      });
    } catch (error) {
      console.error('Pricing error:', error);
      toast.error('Failed to get room details. Please try again.');
    } finally {
      setHotelLoading(null);
    }
  };

  const filterAirports = (query) => {
    // Clear any pending search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Trigger API search with debounce
    searchTimeoutRef.current = setTimeout(() => {
      searchAirportsAPI(query);
    }, 200);
    
    // Return current results
    return airportSearchResults.slice(0, 8);
  };

// City name to airport code mapping for common Indian cities
const CITY_TO_AIRPORT = {
  'DELHI': 'DEL', 'NEW DELHI': 'DEL', 'NEWDELHI': 'DEL',
  'MUMBAI': 'BOM', 'BOMBAY': 'BOM',
  'BANGALORE': 'BLR', 'BENGALURU': 'BLR',
  'CHENNAI': 'MAA', 'MADRAS': 'MAA',
  'KOLKATA': 'CCU', 'CALCUTTA': 'CCU',
  'HYDERABAD': 'HYD',
  'AHMEDABAD': 'AMD',
  'PUNE': 'PNQ',
  'GOA': 'GOI', 'PANAJI': 'GOI',
  'JAIPUR': 'JAI',
  'LUCKNOW': 'LKO',
  'KOCHI': 'COK', 'COCHIN': 'COK',
  'THIRUVANANTHAPURAM': 'TRV', 'TRIVANDRUM': 'TRV',
  'SRINAGAR': 'SXR',
  'VARANASI': 'VNS',
  'PATNA': 'PAT',
  'BHUBANESWAR': 'BBI',
  'GUWAHATI': 'GAU',
  'INDORE': 'IDR',
  'NAGPUR': 'NAG',
  'COIMBATORE': 'CJB',
  'CHANDIGARH': 'IXC',
  'AMRITSAR': 'ATQ',
  // More Indian cities
  'RANCHI': 'IXR',
  'RAIPUR': 'RPR',
  'VISAKHAPATNAM': 'VTZ', 'VIZAG': 'VTZ',
  'MANGALORE': 'IXE',
  'MADURAI': 'IXM',
  'DEHRADUN': 'DED',
  'UDAIPUR': 'UDR',
  'JODHPUR': 'JDH',
  'SURAT': 'STV',
  'VADODARA': 'BDQ', 'BARODA': 'BDQ',
  'RAJKOT': 'RAJ',
  'AGARTALA': 'IXA',
  'IMPHAL': 'IMF',
  'DIBRUGARH': 'DIB',
  'SILCHAR': 'IXS',
  'BAGDOGRA': 'IXB', 'SILIGURI': 'IXB',
  'PORT BLAIR': 'IXZ', 'ANDAMAN': 'IXZ',
  'JAMMU': 'IXJ',
  'LEH': 'IXL', 'LADAKH': 'IXL',
  'AURANGABAD': 'IXU',
  'BHUJ': 'BHJ',
  'TIRUPATI': 'TIR',
  'VIJAYAWADA': 'VGA',
  'HUBLI': 'HBX',
  'BELGAUM': 'IXG',
  'RAJAHMUNDRY': 'RJA',
  'GORAKHPUR': 'GOP',
  'KANPUR': 'KNU',
  'ALLAHABAD': 'IXD', 'PRAYAGRAJ': 'IXD',
  // International - Middle East
  'DUBAI': 'DXB',
  'ABU DHABI': 'AUH',
  'DOHA': 'DOH',
  'RIYADH': 'RUH',
  'JEDDAH': 'JED',
  'MUSCAT': 'MCT',
  'KUWAIT': 'KWI',
  'BAHRAIN': 'BAH',
  'AMMAN': 'AMM',
  'TEL AVIV': 'TLV',
  // International - Southeast Asia
  'SINGAPORE': 'SIN',
  'BANGKOK': 'BKK',
  'KUALA LUMPUR': 'KUL',
  'JAKARTA': 'CGK',
  'BALI': 'DPS',
  'DENPASAR': 'DPS',
  'HO CHI MINH': 'SGN',
  'HANOI': 'HAN',
  'MANILA': 'MNL',
  'PHUKET': 'HKT',
  // International - East Asia
  'TOKYO': 'NRT',
  'NARITA': 'NRT',
  'HANEDA': 'HND',
  'OSAKA': 'KIX',
  'SEOUL': 'ICN',
  'INCHEON': 'ICN',
  'HONG KONG': 'HKG',
  'TAIPEI': 'TPE',
  'BEIJING': 'PEK',
  'SHANGHAI': 'PVG',
  'GUANGZHOU': 'CAN',
  // International - Europe
  'LONDON': 'LHR',
  'HEATHROW': 'LHR',
  'GATWICK': 'LGW',
  'PARIS': 'CDG',
  'FRANKFURT': 'FRA',
  'AMSTERDAM': 'AMS',
  'MUNICH': 'MUC',
  'ZURICH': 'ZRH',
  'ROME': 'FCO',
  'MILAN': 'MXP',
  'MADRID': 'MAD',
  'BARCELONA': 'BCN',
  'VIENNA': 'VIE',
  'BRUSSELS': 'BRU',
  'ISTANBUL': 'IST',
  'ATHENS': 'ATH',
  'DUBLIN': 'DUB',
  'LISBON': 'LIS',
  'MOSCOW': 'SVO',
  'COPENHAGEN': 'CPH',
  'STOCKHOLM': 'ARN',
  'HELSINKI': 'HEL',
  'EDINBURGH': 'EDI',
  'BUCHAREST': 'OTP',
  // International - Americas
  'NEW YORK': 'JFK', 'NEWYORK': 'JFK',
  'LOS ANGELES': 'LAX',
  'SAN FRANCISCO': 'SFO',
  'CHICAGO': 'ORD',
  'WASHINGTON': 'IAD',
  'BOSTON': 'BOS',
  'MIAMI': 'MIA',
  'SEATTLE': 'SEA',
  'TORONTO': 'YYZ',
  'VANCOUVER': 'YVR',
  'MEXICO CITY': 'MEX',
  'SAO PAULO': 'GRU',
  // International - Oceania
  'SYDNEY': 'SYD',
  'MELBOURNE': 'MEL',
  'AUCKLAND': 'AKL',
  // International - Africa
  'JOHANNESBURG': 'JNB',
  'CAPE TOWN': 'CPT',
  'CAIRO': 'CAI',
  'NAIROBI': 'NBO',
  'CASABLANCA': 'CMN',
  // Maldives & Other
  'MALE': 'MLE',
  'MALDIVES': 'MLE',
  'COLOMBO': 'CMB',
  'SRI LANKA': 'CMB',
  'KATHMANDU': 'KTM',
  'DHAKA': 'DAC',
  'MAURITIUS': 'MRU',
  'SEYCHELLES': 'SEZ',
};

// Helper function to get airport code from city name or code
const getAirportCode = (input) => {
  if (!input) return '';
  const upperInput = input.toUpperCase().trim();
  
  // If already a valid 3-letter code, use it
  if (upperInput.length === 3 && /^[A-Z]+$/.test(upperInput)) {
    return upperInput;
  }
  
  // Try to find in mapping
  if (CITY_TO_AIRPORT[upperInput]) {
    return CITY_TO_AIRPORT[upperInput];
  }
  
  // Try partial match
  for (const [city, code] of Object.entries(CITY_TO_AIRPORT)) {
    if (upperInput.includes(city) || city.includes(upperInput)) {
      return code;
    }
  }
  
  // Fallback to first 3 characters
  return upperInput.substring(0, 3);
};

const searchFlights = async () => {
    console.log('searchFlights called!');
    if (!flightForm.origin || !flightForm.destination || !flightForm.departure_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For round-trip, require return date
    if (flightForm.trip_type === 'round-trip' && !flightForm.return_date) {
      toast.error('Please select a return date for round-trip');
      return;
    }
    
    const originCode = getAirportCode(flightForm.origin);
    const destCode = getAirportCode(flightForm.destination);
    
    if (originCode.length < 3 || destCode.length < 3) {
      toast.error('Please enter valid airport codes or city names');
      return;
    }
    
    console.log(`Searching flights: ${flightForm.origin} (${originCode}) -> ${flightForm.destination} (${destCode})`);
    
    setLoading(true);
    setSearchingAnimation(true);
    setSearched(true);
    setActiveTab('flights');
    
    try {
      console.log('Searching flights:', { originCode, destCode, departure: flightForm.departure_date, currency: userCurrency });
      const response = await axios.post(`${API_URL}/api/tripjack/flights/search`, {
        from_city: originCode,
        to_city: destCode,
        departure_date: flightForm.departure_date,
        return_date: flightForm.trip_type === 'round-trip' ? flightForm.return_date : null,
        adults: flightForm.adults,
        cabin_class: flightForm.travel_class,
        currency: userCurrency
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 30000 // 30 second timeout
      });
      
      console.log('API Response:', response.data);
      console.log('Success:', response.data.success);
      console.log('Has tripInfos:', !!response.data.data?.searchResult?.tripInfos);
      
      // Check if TripJack returned success with actual flights
      if (response.data.success && response.data.data?.searchResult?.tripInfos) {
        // Format TripJack response - tripInfos is an object with ONWARD key containing array
        const tripInfosObj = response.data.data?.searchResult?.tripInfos || {};
        const onwardFlights = tripInfosObj.ONWARD || [];
        
        console.log('ONWARD flights count:', onwardFlights.length);
        
        const formattedFlights = onwardFlights.map(trip => {
          // Calculate TOTAL duration for entire journey (all segments)
          const segments = trip.sI || [];
          let totalDurationMins = 0;
          segments.forEach(seg => {
            totalDurationMins += seg.duration || 0;
          });
          // Add layover time (if connecting flight)
          if (segments.length > 1) {
            for (let i = 0; i < segments.length - 1; i++) {
              const arrTime = new Date(segments[i].at);
              const depTime = new Date(segments[i + 1].dt);
              const layoverMins = (depTime - arrTime) / (1000 * 60);
              if (layoverMins > 0) totalDurationMins += layoverMins;
            }
          }
          
          const hours = Math.floor(totalDurationMins / 60);
          const mins = Math.round(totalDurationMins % 60);
          const durationStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          
          // Extract time from datetime string (format: "2026-09-15T05:35")
          const depDateTime = segments[0]?.dt || '';
          const arrDateTime = segments[segments.length - 1]?.at || '';
          const depTime = depDateTime.split('T')[1] || depDateTime;
          const arrTime = arrDateTime.split('T')[1] || arrDateTime;
          
          // Build connecting route display (e.g., "DEL → BOM → LHR")
          const routeSegments = segments.map(s => s.da?.code).concat([segments[segments.length - 1]?.aa?.code]);
          const routeDisplay = routeSegments.filter(Boolean).join(' → ');
          
          // Get all airlines if multiple (for codeshare)
          const airlines = [...new Set(segments.map(s => s.fD?.aI?.name).filter(Boolean))];
          const airlineDisplay = airlines.length > 1 ? airlines.join(' + ') : airlines[0] || 'Unknown Airline';
          
          return {
            airline: airlineDisplay,
            airline_name: airlineDisplay,
            airline_code: segments[0]?.fD?.aI?.code || '',
            flight_number: segments.map(s => s.fD?.fN).filter(Boolean).join('/'),
            departure: depDateTime,
            arrival: arrDateTime,
            departure_time: depTime,
            arrival_time: arrTime,
            origin: segments[0]?.da?.code || originCode,
            origin_city: segments[0]?.da?.city || '',
            destination: segments[segments.length - 1]?.aa?.code || destCode,
            destination_city: segments[segments.length - 1]?.aa?.city || '',
            duration: durationStr,
            durationMins: totalDurationMins,
            price: Math.round(trip.totalPriceList?.[0]?.fd?.ADULT?.fC?.TF || 0),
            currency: userCurrency,
            stops: segments.length - 1,
            stopCities: segments.length > 1 ? segments.slice(0, -1).map(s => s.aa?.city || s.aa?.code).join(', ') : null,
            routeDisplay: routeDisplay,
            baggage: trip.totalPriceList?.[0]?.fd?.ADULT?.bI || {},
            price_id: trip.totalPriceList?.[0]?.id,
            priceId: trip.totalPriceList?.[0]?.id,
            isLcc: segments[0]?.fD?.aI?.isLcc || false,
            segments: segments // Keep raw segments for detailed view
          };
        });
        
        if (formattedFlights.length > 0) {
          setResults(formattedFlights);
          toast.success(`Found ${formattedFlights.length} flights`);
        } else {
          toast.info('No flights found for this route. Try different dates or cities.');
          setResults([]);
        }
      } else {
        // Check if it's an access denied error
        const errors = response.data.data?.errors || [];
        const isAccessDenied = errors.some(e => e.errCode === '408' || e.message?.includes('Access Denied'));
        
        if (isAccessDenied) {
          toast.error('Flight API requires activation. Please contact TripJack support.');
        } else {
          toast.info('No flights found for this route. Try different dates or cities.');
        }
        setResults([]);
      }
    } catch (err) {
      console.error('Flight search error:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Search is taking longer than expected. Please try again.');
      } else if (err.response?.status === 403) {
        toast.error('Flight search temporarily unavailable. Please try again later.');
      } else {
        toast.error('Flight search failed. Please try again.');
      }
      setResults([]);
    } finally {
      setLoading(false);
      setSearchingAnimation(false);
    }
  };

  const searchHotels = async () => {
    if (!hotelForm.city || !hotelForm.check_in || !hotelForm.check_out) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    setActiveTab('hotels');
    
    try {
      // Use TripJack hotel API
      const cityCode = hotelForm.city.split(',')[0].trim().toUpperCase();
      const response = await axios.get(`${API_URL}/api/tripjack/hotels/search`, {
        params: {
          city_code: cityCode,
          checkin_date: hotelForm.check_in,
          checkout_date: hotelForm.check_out,
          adults: hotelForm.guests,
          children: 0
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        const tripjackHotels = response.data.data?.hotels || [];
        // CRITICAL: Store correlationId for booking flow
        const correlationId = response.data.data?.correlationId || response.data.correlation_id;
        
        // Format hotels for display - include all required booking fields
        const formattedHotels = tripjackHotels.map(h => {
          const firstOption = h.options?.[0];
          return {
            id: h.hotelId || h.tjHotelId || h.id,
            hotelId: h.hotelId || h.tjHotelId || h.id,  // For booking
            name: h.name,
            address: h.ad || '',
            city: hotelForm.city,
            rating: h.rt || 0,
            stars: h.star || 3,
            star_rating: h.star || 3,
            // Use normalized image format from backend
            image: h.primary_image || h.img?.[0]?.url || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
            thumbnail: h.thumbnail || h.img?.[0]?.thumbnail,
            has_images: h.has_images !== false,
            price: firstOption?.pricing?.totalPrice || 0,
            price_per_night: firstOption?.pricing?.totalPrice || 0,
            currency: firstOption?.pricing?.currency || 'INR',
            optionId: firstOption?.optionId,  // For booking
            roomType: firstOption?.roomInfo?.[0]?.name || 'Standard Room',
            mealBasis: firstOption?.mealBasis || 'Room Only',
            isRefundable: firstOption?.cancellation?.isRefundable || false,
            amenities: h.facilities || [],
            source: 'TripJack',
            // CRITICAL: Store correlationId and search params for booking flow
            correlationId: correlationId,
            searchParams: {
              checkinDate: hotelForm.check_in,
              checkoutDate: hotelForm.check_out,
              adults: hotelForm.guests,
              rooms: hotelForm.rooms || 1
            }
          };
        });
        
        setResults(formattedHotels);
        if (formattedHotels.length > 0) {
          toast.success(`Found ${formattedHotels.length} hotels`);
        } else {
          toast.info('No hotels found for this destination');
        }
      } else {
        toast.error(response.data.error || 'No hotels found');
        setResults([]);
      }
    } catch (err) {
      console.error('Hotel search error:', err);
      toast.error('Failed to search hotels. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Handler for AI Concierge flight search
  const handleConciergeFlightSearch = (params) => {
    // Update flight form with concierge-parsed data
    setFlightForm({
      origin: params.origin || '',
      destination: params.destination || '',
      departure_date: params.departure_date || '',
      return_date: params.return_date || '',
      adults: params.adults || 1,
      travel_class: params.travel_class || 'ECONOMY'
    });
    setActiveTab('flights');
    // Auto-trigger search after a brief delay to let state update
    setTimeout(() => {
      searchFlights();
    }, 100);
  };

  // Handler for AI Concierge hotel search
  const handleConciergeHotelSearch = (params) => {
    // Update hotel form with concierge-parsed data
    setHotelForm({
      city: params.destination || '',
      check_in: params.check_in || '',
      check_out: params.check_out || '',
      guests: params.guests || 2,
      rooms: params.rooms || 1
    });
    setActiveTab('hotels');
    // Auto-trigger search after a brief delay to let state update
    setTimeout(() => {
      searchHotels();
    }, 100);
  };

  // Collection tabs
  const collectionTabs = [
    { id: 'trending', label: 'Trending', icon: Sparkles },
    { id: 'beaches', label: 'Beaches', icon: Umbrella },
    { id: 'luxury', label: 'Luxury', icon: Star },
    { id: 'culture', label: 'Culture', icon: Castle },
    { id: 'adventure', label: 'Adventure', icon: Mountain },
    { id: 'family', label: 'Family', icon: Baby },
    { id: 'hidden', label: 'Hidden Gems', icon: Compass },
  ];

  return (
    <div className="min-h-screen bg-[#09080E]" data-testid="premium-travel-section">
      {/* Floating Back Button */}
      <FloatingBackButton />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-[90vh] min-h-[700px] overflow-hidden">
        {/* Dynamic Background */}
        <motion.div 
          key={heroImage}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/70 via-transparent to-[#09080E]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09080E]/60 via-transparent to-[#09080E]/60" />
        </motion.div>

        {/* Destination Info Overlay */}
        <AnimatePresence>
          {destinationInfo && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="absolute top-32 left-8 md:left-16 z-10"
            >
              <p className="text-[#00E5FF] text-sm uppercase tracking-[0.3em] mb-2 font-medium">
                Destination Preview
              </p>
              <h2 className="font-['Playfair_Display'] text-5xl md:text-7xl text-white font-bold mb-2">
                {destinationInfo.name}
              </h2>
              <p className="text-white/70 text-xl mb-2">{destinationInfo.country}</p>
              {destinationInfo.vibe && (
                <p className="text-white/50 text-sm italic mb-6">"{destinationInfo.vibe}"</p>
              )}
              
              {/* Real-time Weather Widget */}
              <div className="mb-4 max-w-md">
                <WeatherWidget destination={destinationInfo.name} showForecast={false} />
              </div>
              
              <div className="flex flex-wrap gap-3">
                <div className="bg-black/40 backdrop-blur-xl px-4 py-2 rounded-full border border-white/10 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-white text-sm">Best: {destinationInfo.bestTime}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-4 md:px-16">
          {/* Editorial Title */}
          {!destinationInfo && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-10"
            >
              <p className="text-[#00E5FF] text-sm uppercase tracking-[0.3em] mb-4">
                Travel & Stay
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight max-w-4xl">
                Discover Extraordinary <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00E5FF] via-[#FF007F] to-[#00E5FF]">
                  Destinations
                </span>
              </h1>
              <p className="text-white/60 text-lg mt-4 max-w-xl">
                Curated travel experiences that inspire wonder and create lasting memories
              </p>
            </motion.div>
          )}

          {/* Glass Booking Widget */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl max-w-5xl"
          >
            {/* Main Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-2">
              {[
                { id: 'explore', label: 'Explore', icon: Globe },
                { id: 'flights', label: 'Flights', icon: Plane },
                { id: 'hotels', label: 'Hotels', icon: Building2 },
                { id: 'tours', label: 'Tours', icon: Camera },
                { id: 'packages', label: 'Packages', icon: Map },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'tours') {
                      navigate('/tours');
                    } else {
                      setActiveTab(tab.id); 
                      setSearched(false); 
                      setResults([]);
                    }
                  }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#3E2A86] to-[#5438B5] text-white shadow-lg shadow-[#3E2A86]/30'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`${tab.id}-tab`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Flight Search Form */}
            {activeTab === 'flights' && (
              <div className="space-y-4">
                {/* Trip Type Toggle */}
                <div className="flex items-center gap-4 mb-4">
                  <button
                    onClick={() => setFlightForm({...flightForm, trip_type: 'one-way', return_date: ''})}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      flightForm.trip_type === 'one-way'
                        ? 'bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-white'
                        : 'bg-white/5 text-white/60 hover:text-white border border-white/10'
                    }`}
                    data-testid="one-way-btn"
                  >
                    One-Way
                  </button>
                  <button
                    onClick={() => setFlightForm({...flightForm, trip_type: 'round-trip'})}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      flightForm.trip_type === 'round-trip'
                        ? 'bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-white'
                        : 'bg-white/5 text-white/60 hover:text-white border border-white/10'
                    }`}
                    data-testid="round-trip-btn"
                  >
                    Round-Trip
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                  {/* Origin */}
                  <div className="relative lg:col-span-1">
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">From</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#00E5FF]" />
                      <input
                        type="text"
                        value={flightForm.origin}
                        onChange={(e) => {
                          setFlightForm({...flightForm, origin: e.target.value});
                          setShowOriginSuggestions(true);
                        }}
                        onFocus={() => setShowOriginSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowOriginSuggestions(false), 200)}
                        placeholder="City or Airport Code"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                        data-testid="flight-origin-input"
                      />
                    {showOriginSuggestions && filterAirports(flightForm.origin).length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                        {filterAirports(flightForm.origin).map((airport) => (
                          <button
                            key={airport.code}
                            onClick={() => {
                              setFlightForm({...flightForm, origin: airport.code});
                              setShowOriginSuggestions(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="text-white font-medium">{airport.city}</span>
                                <span className="text-white/50 ml-2 text-sm">{airport.name}</span>
                              </div>
                              <span className="text-[#00E5FF] font-mono">{airport.code}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Destination */}
                <div className="relative lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">To</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF007F]" />
                    <input
                      type="text"
                      value={flightForm.destination}
                      onChange={(e) => {
                        setFlightForm({...flightForm, destination: e.target.value});
                        setShowDestSuggestions(true);
                      }}
                      onFocus={() => setShowDestSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowDestSuggestions(false), 200)}
                      placeholder="City or Airport"
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all"
                      data-testid="flight-destination-input"
                    />
                    {showDestSuggestions && filterAirports(flightForm.destination).length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                        {filterAirports(flightForm.destination).map((airport) => (
                          <button
                            key={airport.code}
                            onClick={() => {
                              setFlightForm({...flightForm, destination: airport.code});
                              setShowDestSuggestions(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="text-white font-medium">{airport.city}</span>
                                <span className="text-white/50 ml-2 text-sm">{airport.name}</span>
                              </div>
                              <span className="text-[#FF007F] font-mono">{airport.code}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates & Search */}
                <div className="lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Departure</label>
                  <input type="date" value={flightForm.departure_date} onChange={(e) => setFlightForm({...flightForm, departure_date: e.target.value})} min={today} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" data-testid="flight-departure-date" />
                </div>
                {flightForm.trip_type === 'round-trip' && (
                  <div className="lg:col-span-1">
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Return</label>
                    <input type="date" value={flightForm.return_date} onChange={(e) => setFlightForm({...flightForm, return_date: e.target.value})} min={flightForm.departure_date || today} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" data-testid="flight-return-date" />
                  </div>
                )}
                <div className="lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Travelers</label>
                  <select value={flightForm.adults} onChange={(e) => setFlightForm({...flightForm, adults: parseInt(e.target.value)})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none">
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n} className="bg-[#12101A]">{n} Adult{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div className="lg:col-span-1 flex items-end">
                  <button onClick={searchFlights} disabled={loading} className="w-full bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black font-semibold py-3 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="search-flights-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Search
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* Hotel Search Form */}
            {activeTab === 'hotels' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="relative lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Destination</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#00E5FF]" />
                    <input type="text" value={hotelForm.city} onChange={(e) => { setHotelForm({...hotelForm, city: e.target.value}); setShowCitySuggestions(true); }} onFocus={() => setShowCitySuggestions(true)} onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)} placeholder="City" className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#00E5FF] transition-all" data-testid="hotel-city-input" />
                    {showCitySuggestions && filterAirports(hotelForm.city).length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                        {filterAirports(hotelForm.city).map((airport) => (
                          <button key={airport.code} onClick={() => { setHotelForm({...hotelForm, city: airport.city}); setShowCitySuggestions(false); }} className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                            <span className="text-white font-medium">{airport.city}</span>
                            <span className="text-white/50 ml-2 text-sm">{airport.country}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Check-in</label>
                  <input type="date" value={hotelForm.check_in} onChange={(e) => setHotelForm({...hotelForm, check_in: e.target.value})} min={today} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div className="lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Check-out</label>
                  <input type="date" value={hotelForm.check_out} onChange={(e) => setHotelForm({...hotelForm, check_out: e.target.value})} min={hotelForm.check_in || today} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div className="lg:col-span-1">
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Guests</label>
                  <select value={hotelForm.guests} onChange={(e) => setHotelForm({...hotelForm, guests: parseInt(e.target.value)})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none">
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n} className="bg-[#12101A]">{n} Guest{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div className="lg:col-span-1 flex items-end">
                  <button onClick={searchHotels} disabled={loading} className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-3 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50" data-testid="search-hotels-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Search
                  </button>
                </div>
              </div>
            )}

            {/* Explore Tab - Quick Search */}
            {(activeTab === 'explore' || activeTab === 'packages') && (
              <div className="text-center py-6">
                <p className="text-white/60 mb-6">Where would you like to go?</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {['Dubai', 'Bali', 'Paris', 'Maldives', 'Tokyo', 'Singapore'].map(city => (
                    <button
                      key={city}
                      onClick={() => {
                        setFlightForm({...flightForm, destination: city});
                        setActiveTab('flights');
                      }}
                      className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-white/20 transition-all"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ==================== SEARCH RESULTS ==================== */}
      <AnimatePresence>
        {searched && (
          <motion.section initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="px-4 md:px-16 py-16 bg-[#09080E]">
            {searchingAnimation ? (
              <div className="flex flex-col items-center justify-center py-16 min-h-[400px]">
                {/* Premium Flight Takeoff Animation */}
                <div className="relative w-full max-w-md h-48 mb-8 overflow-hidden">
                  {/* Sky gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl" />
                  
                  {/* Stars */}
                  {[...Array(20)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 bg-white rounded-full"
                      style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 60}%` }}
                      animate={{ opacity: [0.2, 1, 0.2] }}
                      transition={{ duration: 1 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
                    />
                  ))}
                  
                  {/* Runway */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-gray-700 rounded-full">
                    <div className="absolute inset-0 flex items-center justify-around">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="w-4 h-0.5 bg-yellow-400/60" />
                      ))}
                    </div>
                  </div>
                  
                  {/* Animated Airplane - Takeoff */}
                  <motion.div
                    className="absolute bottom-8 left-8"
                    initial={{ x: 0, y: 0, rotate: 0 }}
                    animate={{ 
                      x: [0, 50, 150, 300, 400],
                      y: [0, -10, -50, -120, -200],
                      rotate: [0, -5, -15, -25, -30],
                      scale: [1, 1.1, 1, 0.8, 0.5]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="relative">
                      {/* Airplane body */}
                      <Plane className="w-16 h-16 text-white drop-shadow-[0_0_15px_rgba(0,229,255,0.8)]" />
                      {/* Engine trail */}
                      <motion.div
                        className="absolute -left-8 top-1/2 -translate-y-1/2 w-12 h-2 bg-gradient-to-l from-orange-500 via-yellow-400 to-transparent rounded-full blur-sm"
                        animate={{ opacity: [0.8, 1, 0.6], scaleX: [1, 1.5, 1] }}
                        transition={{ duration: 0.3, repeat: Infinity }}
                      />
                    </div>
                  </motion.div>
                  
                  {/* Clouds moving */}
                  <motion.div
                    className="absolute top-8 right-0"
                    animate={{ x: [-100, -500] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  >
                    <Cloud className="w-16 h-16 text-white/10" />
                  </motion.div>
                  <motion.div
                    className="absolute top-16 right-20"
                    animate={{ x: [-50, -450] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  >
                    <Cloud className="w-12 h-12 text-white/10" />
                  </motion.div>
                </div>
                
                {/* Loading text */}
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <motion.p 
                    className="text-white text-xl font-medium mb-2"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    ✈️ Searching flights worldwide...
                  </motion.p>
                  <p className="text-white/50 text-sm">Finding the best deals across all airlines</p>
                  <motion.div 
                    className="flex justify-center gap-1 mt-4"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#00E5FF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </motion.div>
                </motion.div>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-[#00E5FF] animate-spin mb-4" />
                <p className="text-white/60">Loading results...</p>
              </div>
            ) : results.length > 0 ? (
              <>
                <SectionHeader eyebrow="Search Results" title={`${results.length} ${activeTab === 'flights' ? 'Flights' : 'Hotels'} Found`} />
                <div className="grid gap-6">
                  {activeTab === 'flights' ? results.map((flight, index) => (
                    <motion.div key={flight.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-[#00E5FF]/30 transition-all group">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center"><Plane className="w-7 h-7 text-[#00E5FF]" /></div>
                          <div>
                            <p className="text-white font-semibold text-lg">{flight.airline_name || flight.airline}</p>
                            <p className="text-white/50 text-sm">{flight.flight_number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-white text-2xl font-bold">{flight.departure_time}</p>
                            <p className="text-white/50 text-sm">{flight.origin}</p>
                          </div>
                          <div className="flex flex-col items-center px-4 min-w-[120px]">
                            <p className="text-white/40 text-xs mb-1">{flight.duration}</p>
                            <div className="w-full h-px bg-gradient-to-r from-[#00E5FF] to-[#FF007F] relative">
                              <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white" />
                              {/* Stop indicators */}
                              {flight.stops > 0 && [...Array(Math.min(flight.stops, 3))].map((_, i) => (
                                <div 
                                  key={i} 
                                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-[#FF007F] rounded-full border border-white/50"
                                  style={{ left: `${((i + 1) / (flight.stops + 1)) * 100}%` }}
                                />
                              ))}
                            </div>
                            <p className={`text-xs mt-1 ${flight.stops === 0 ? 'text-green-400' : 'text-[#FF007F]'}`}>
                              {flight.stops === 0 ? '✓ Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                            </p>
                            {flight.stopCities && (
                              <p className="text-white/30 text-[10px] mt-0.5">via {flight.stopCities}</p>
                            )}
                          </div>
                          <div className="text-center">
                            <p className="text-white text-2xl font-bold">{flight.arrival_time}</p>
                            <p className="text-white/50 text-sm">{flight.destination}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#00E5FF] text-3xl font-bold">{currencySymbol}{flight.price?.toLocaleString()}</p>
                          <p className="text-white/40 text-sm mb-3">per person</p>
                          <button 
                            onClick={() => navigate('/complete-booking', { 
                              state: { 
                                type: 'flight', 
                                selection: {
                                  priceId: flight.priceId,
                                  priceIds: [flight.priceId],
                                  airline: flight.airline_name || flight.airline,
                                  flightNumber: flight.flight_number,
                                  origin: flight.origin,
                                  destination: flight.destination,
                                  departureTime: flight.departure_time,
                                  arrivalTime: flight.arrival_time,
                                  duration: flight.duration,
                                  stops: flight.stops,
                                  price: flight.price,
                                  currency: userCurrency,
                                  currencySymbol: currencySymbol
                                }
                              }
                            })}
                            className="bg-gradient-to-r from-[#3E2A86] to-[#5438B5] text-white px-6 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(84,56,181,0.4)] transition-all"
                            data-testid={`select-flight-${index}`}
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )) : results.map((hotel, index) => (
                    <motion.div key={hotel.id || index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF007F]/30 transition-all group">
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-72 h-48 md:h-auto relative overflow-hidden">
                          <img src={hotel.image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80'} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full"><div className="flex items-center gap-1">{[...Array(hotel.stars || 4)].map((_, i) => <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />)}</div></div>
                        </div>
                        <div className="flex-1 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div><h3 className="text-white text-xl font-semibold mb-1">{hotel.name}</h3><p className="text-white/50 flex items-center gap-1"><MapPin className="w-4 h-4" /> {hotel.city}</p></div>
                            {hotel.rating && <div className="bg-[#00E5FF]/20 text-[#00E5FF] px-3 py-1 rounded-lg font-semibold">{hotel.rating}/10</div>}
                          </div>
                          <div className="flex items-end justify-between">
                            <div><p className="text-white/40 text-sm">Starting from</p><p className="text-[#FF007F] text-3xl font-bold">{hotel.currency} {hotel.price_per_night?.toLocaleString()}</p><p className="text-white/40 text-sm">per night</p></div>
                            <button 
                              onClick={() => handleSelectHotel(hotel)}
                              disabled={hotelLoading === (hotel.hotelId || hotel.id)}
                              className={`bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white px-6 py-2 rounded-xl transition-all flex items-center gap-2 ${
                                hotelLoading === (hotel.hotelId || hotel.id) 
                                  ? 'opacity-70 cursor-wait' 
                                  : 'hover:shadow-[0_0_20px_rgba(255,0,127,0.4)]'
                              }`}
                              data-testid={`select-hotel-${index}`}
                            >
                              {hotelLoading === (hotel.hotelId || hotel.id) ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Loading...
                                </>
                              ) : (
                                'View Rooms'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Globe className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-white text-xl font-semibold mb-2">No Results Found</h3>
                <p className="text-white/50">Try adjusting your search criteria</p>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ==================== COLLECTION TABS ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-8 bg-[#09080E] sticky top-0 z-40">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {collectionTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveCollection(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap backdrop-blur-xl ${
                  activeCollection === tab.id
                    ? 'bg-gradient-to-r from-[#FF007F]/30 to-[#00E5FF]/30 text-white border border-white/20 shadow-[0_0_25px_rgba(255,0,127,0.3)]'
                    : 'bg-white/[0.05] text-white/60 border border-white/[0.08] hover:bg-white/[0.1] hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ==================== TRENDING DESTINATIONS ==================== */}
      {!searched && activeCollection === 'trending' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Curated Collections" title="Trending Destinations" subtitle="Most loved destinations this season" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRENDING_DESTINATIONS.map((dest, index) => (
              <DestinationCard key={dest.code} destination={dest} size={index === 0 ? 'large' : 'normal'} onClick={() => setFlightForm({...flightForm, destination: dest.code})} />
            ))}
          </div>
        </section>
      )}

      {/* ==================== BEACH DESTINATIONS ==================== */}
      {!searched && activeCollection === 'beaches' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Sun, Sand & Sea" title="Beach Destinations" subtitle="Escape to paradise" accentColor="#00E5FF" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {BEACH_DESTINATIONS.map((dest, index) => (
              <motion.div key={dest.city} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="relative overflow-hidden rounded-2xl cursor-pointer group min-h-[300px]">
                <img src={dest.image} alt={dest.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4"><span className="bg-[#00E5FF]/80 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{dest.beach}</span></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mb-1">{dest.city}</h3>
                  <p className="text-white/60 text-sm mb-2">{dest.country}</p>
                  <p className="text-[#00E5FF] font-semibold">From {dest.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== LUXURY ESCAPES ==================== */}
      {!searched && activeCollection === 'luxury' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="Indulge in Opulence" title="Luxury Escapes" subtitle="Extraordinary experiences for the discerning traveler" accentColor="#FFD700" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {LUXURY_ESCAPES.map((dest, index) => (
              <motion.div key={dest.city} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="relative overflow-hidden rounded-3xl cursor-pointer group">
                <div className="aspect-[4/5] relative">
                  <img src={dest.image} alt={dest.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute top-6 left-6 flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#FFD700] fill-[#FFD700]" />
                    <span className="text-[#FFD700] text-sm font-medium uppercase tracking-wider">Luxury</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <span className="text-white/60 text-sm">{dest.type}</span>
                    <h3 className="font-['Playfair_Display'] text-white text-3xl font-bold mt-1 mb-2">{dest.city}</h3>
                    <p className="text-white/60 mb-4">{dest.country} • {dest.nights}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-[#FFD700] text-2xl font-bold">{dest.price}</p>
                      <button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white px-5 py-2 rounded-full hover:bg-white/20 transition-all">View Details</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== CULTURAL JOURNEYS ==================== */}
      {!searched && activeCollection === 'culture' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Immerse Yourself" title="Cultural Journeys" subtitle="Discover the soul of destinations through their heritage" accentColor="#FF007F" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {CULTURAL_JOURNEYS.map((dest, index) => (
              <motion.div key={dest.city} initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex flex-col md:flex-row bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FF007F]/30 transition-all">
                <div className="md:w-1/2 h-64 md:h-auto relative overflow-hidden">
                  <img src={dest.image} alt={dest.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <span className="text-[#FF007F] text-sm uppercase tracking-wider mb-2">{dest.duration}</span>
                  <h3 className="font-['Playfair_Display'] text-white text-3xl font-bold mb-2">{dest.city}</h3>
                  <p className="text-white/60 mb-1">{dest.country}</p>
                  <p className="text-white/80 mb-6">{dest.highlight}</p>
                  <button className="self-start bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white px-6 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,0,127,0.4)] transition-all">Explore Journey</button>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== ADVENTURE EXPERIENCES ==================== */}
      {!searched && activeCollection === 'adventure' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="For the Bold" title="Adventure Experiences" subtitle="Thrilling journeys for the adventurous soul" accentColor="#00E5FF" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ADVENTURE_EXPERIENCES.map((exp, index) => (
              <motion.div key={exp.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="relative overflow-hidden rounded-2xl cursor-pointer group aspect-[3/4]">
                <img src={exp.image} alt={exp.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute top-4 left-4"><span className="bg-[#00E5FF]/80 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{exp.type}</span></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-['Playfair_Display'] text-white text-xl font-bold mb-1">{exp.title}</h3>
                  <p className="text-white/60 text-sm mb-2">{exp.location}</p>
                  <p className="text-[#00E5FF] font-semibold">{exp.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== FAMILY DESTINATIONS ==================== */}
      {!searched && activeCollection === 'family' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Create Memories Together" title="Family Destinations" subtitle="Perfect getaways for families of all sizes" accentColor="#FF007F" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FAMILY_DESTINATIONS.map((dest, index) => (
              <motion.div key={dest.city} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FF007F]/30 transition-all">
                <div className="h-48 relative overflow-hidden">
                  <img src={dest.image} alt={dest.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4"><span className="bg-[#FF007F] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full">{dest.tag}</span></div>
                </div>
                <div className="p-5">
                  <h3 className="text-white text-xl font-bold mb-1">{dest.city}</h3>
                  <p className="text-white/60 text-sm mb-2">{dest.country}</p>
                  <p className="text-white/80 text-sm">{dest.attraction}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== HIDDEN GEMS ==================== */}
      {!searched && activeCollection === 'hidden' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="Off the Beaten Path" title="Hidden Gems" subtitle="Discover destinations few have explored" accentColor="#FFD700" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HIDDEN_GEMS.map((dest, index) => (
              <motion.div key={dest.city} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FFD700]/30 transition-all">
                <div className="h-56 relative overflow-hidden">
                  <img src={dest.image} alt={dest.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4"><Compass className="w-6 h-6 text-[#FFD700]" /></div>
                </div>
                <div className="p-5">
                  <h3 className="text-white text-xl font-bold mb-1">{dest.city}</h3>
                  <p className="text-white/60 text-sm mb-2">{dest.country}</p>
                  <p className="text-white/80 text-sm italic mb-3">"{dest.desc}"</p>
                  <p className="text-[#FFD700] font-semibold">From {dest.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== WEEKEND GETAWAYS ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Escape the City" title="Weekend Getaways" subtitle="Quick escapes just a few hours away" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {WEEKEND_GETAWAYS.map((getaway, index) => (
              <motion.div key={getaway.city} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="relative overflow-hidden rounded-2xl cursor-pointer group aspect-[3/4]">
                <img src={getaway.image} alt={getaway.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4"><span className="bg-black/50 backdrop-blur-xl text-white text-xs px-2 py-1 rounded-full">{getaway.duration}</span></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-['Playfair_Display'] text-white text-xl font-bold mb-1">{getaway.city}</h3>
                  <p className="text-[#00E5FF] font-semibold">{getaway.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== TRAVEL STORIES ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-20 bg-[#0D0B14]">
          <SectionHeader eyebrow="Travel Stories" title="Editorial Inspiration" subtitle="Curated stories to fuel your wanderlust" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TRAVEL_STORIES.map((story, index) => (
              <motion.article key={story.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="group cursor-pointer">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5 relative">
                  <img src={story.image} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4"><span className="bg-white/90 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{story.category}</span></div>
                </div>
                <span className="text-white/40 text-sm">{story.readTime}</span>
                <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mt-2 mb-3 group-hover:text-[#00E5FF] transition-colors">{story.title}</h3>
                <p className="text-white/60 text-sm">By {story.author}</p>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* ==================== INSPIRATION QUOTE ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-32 bg-[#09080E] text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Compass className="w-12 h-12 text-[#00E5FF] mx-auto mb-8" />
            <blockquote className="font-['Playfair_Display'] text-3xl md:text-5xl text-white font-bold max-w-4xl mx-auto leading-tight mb-8">
              "The world is a book and those who do not travel read only one page."
            </blockquote>
            <p className="text-white/50 uppercase tracking-widest">— Saint Augustine</p>
          </motion.div>
        </section>
      )}

      {/* AI Travel Concierge replaced by the global unified AIRA assistant (GlobalVoiceAssistant) */}
    </div>
  );
}
